import React, { useMemo } from 'react'
import { Checkbox, Form } from '@carsys/form'
import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import * as Yup from 'yup'
import { useUnauthorizedUserThemeContext } from 'app/auth/components/UnauthorizedUserThemeContextProvider'
import { ShopCountry } from '@cbgms/base/shop-id'
import { useGetTermsAndConditionsForCountryAndModules } from 'utils/use-get-terms-and-conditions-url'
import { Subscriptions } from '@carsys/enums/subscription'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import { getDocumentURLForShopAndLocale } from 'app/menu/components/TopBarV1/setupWizard/SetupWizardStepHaynesProEula'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'

export const OnboardingAcceptTermsAndConditions: React.FC<
  defaultStepFormProps & {
    onSubmit: (v: FormValues) => void
    country: string
    modules: Subscriptions[]
  }
> = ({
  onSubmit,
  country,
  modules,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData
}) => {
  const { t, i18n } = useTranslation()
  const initialValues = useMemo(() => ({ AcceptedEULA: false, AcceptedHaynesPro: false, ...initialData } as FormValues), [])
  const termsAndConditionsUrl = useGetTermsAndConditionsForCountryAndModules(modules, country)
  const { applicationName: appName, environmentName: envName } = useUnauthorizedUserThemeContext()

  const haynesEULAURL = useMemo(() => {
    const lang = i18n.language?.split('-')[0] // pick only the language
    return getDocumentURLForShopAndLocale(country as ShopCountry, lang)
  }, [country, i18n.language])

  const validationSchema = Yup.object().shape({
    AcceptedEULA: Yup.boolean().oneOf([true]),
    AcceptedHaynesPro: Yup.boolean().oneOf([true])
  })

  const formSubmit = async (v: FormValues) => {
    onSubmit(v)
    return v
  }

  return (
    <BaseStepLayout
      title={t('onboarding.terms_and_conditions.tac_step_header')}
      subTitles={[t('onboarding.terms_and_conditions.tac_step_info', { appName, envName })]}
    >
      <Form
        onSubmit={formSubmit}
        onSubmitSuccess={onSubmitSuccess}
        initialValues={initialValues}
        NavigationConfirmation={null}
        validationSchema={validationSchema}
      >
        {({ values, isSubmitting, handleSubmit }) => {
          const cannotSubmit = !values.AcceptedEULA || !values.AcceptedHaynesPro
          return (
            <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
              <Box display='flex' flexWrap='nowrap' justifyContent='space-between' mt={1}>
                <Box m={1} ml={0} flex='1 1 auto'>
                  <Box mb={1}>
                    <Typography variant='h6'>{`${appName}`}</Typography>
                  </Box>
                  <Box>
                    <a href={termsAndConditionsUrl} target='_blank' rel='nofollow'>
                      {t('global.terms_and_conditions_click_here', { appName })}
                    </a>
                  </Box>
                  <Box mt={1}>
                    <Checkbox
                      name='AcceptedEULA'
                      label={t('onboarding.terms_and_conditions.accept_terms_and_condition_checkbox_label', { name: appName })}
                    />
                  </Box>
                </Box>
                <Divider orientation='vertical' variant='middle' flexItem />
                <Box m={1} ml={2} flex='1 1 auto'>
                  <Box mb={1}>
                    <Typography variant='h6'>HaynesPro</Typography>
                  </Box>
                  <Box>
                    <a href={haynesEULAURL} target='_blank'>
                      {t('SetupWizard.haynes_pro_eula_click_here')}
                    </a>
                  </Box>
                  <Box mt={1}>
                    <Checkbox
                      name='AcceptedHaynesPro'
                      label={t('onboarding.terms_and_conditions.accept_terms_and_condition_checkbox_label', { name: 'Haynes-Pro' })}
                    />
                  </Box>
                </Box>
              </Box>

              <StepNavigation
                prevStep={prevStep}
                hideBack={isFirstStep}
                disableNext={cannotSubmit}
                isLoading={isSubmitting || isPersisting}
                hideSkip={hideSkip}
              />
            </WithRunInBackgroundHelper>
          )
        }}
      </Form>
    </BaseStepLayout>
  )
}

export default OnboardingAcceptTermsAndConditions
